/**
 * Normalises a bare hostname for host-scoped comparisons — trimmed,
 * lower-cased, and stripped of a leading `www.`. Used on both sides of a match
 * (the embed host and a saved listing's recorded host) so they line up.
 *
 * @param {string} host - a bare hostname (e.g. `www.Rentals.ca`)
 * @returns {string} the normalised hostname (empty string for empty input)
 */
const normalizeHostname = (host: string): string => {
  const trimmed = typeof host === 'string' ? host.trim() : ''

  return trimmed.toLowerCase().replace(/^www\./u, '')
}

/**
 * Extracts the normalised hostname from a listing URL for host-scoped
 * comparisons — lower-cased and stripped of a leading `www.`. Returns null for
 * empty or unparseable input. Pairs with `getEmbedHostname()` so a saved
 * listing's `sourceUrl` can be matched against the host site the SDK overlay is
 * embedded on.
 *
 * @param {string} url - the listing URL (e.g. an ImportedListing.sourceUrl)
 * @returns {string | null} the bare hostname, or null
 */
const extractListingHostname = (url: string): string | null => {
  const trimmed = typeof url === 'string' ? url.trim() : ''

  if (trimmed === '') {
    return null
  }

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return null
  }

  const hostname = normalizeHostname(parsed.hostname)

  return hostname === '' ? null : hostname
}

export { normalizeHostname, extractListingHostname }
